/**
 * About page - Green/black theme, PublicPageLayout.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../providers';
import SEO from '../components/seo/SEO';
import { generateOrganizationSchema } from '../utils/seo';
import { PublicPageLayout } from '../components/public';

const About = () => {
  const navigate = useNavigate();
  const { language } = useI18n();

  const values = [
    { icon: '🔒', title: language === 'fr' ? 'Sécurité' : 'Security', description: language === 'fr' ? 'Votre argent est notre priorité. Nous utilisons les meilleures pratiques de sécurité pour protéger vos transactions.' : 'Your money is our priority. We use best-in-class security practices to protect your transactions.' },
    { icon: '🤝', title: language === 'fr' ? 'Confiance' : 'Trust', description: language === 'fr' ? 'Nous construisons la confiance en facilitant des transactions équitables et transparentes pour tous.' : 'We build trust by facilitating fair and transparent transactions for everyone.' },
    { icon: '⚡', title: language === 'fr' ? 'Simplicité' : 'Simplicity', description: language === 'fr' ? 'Notre philosophie "3 Taps to Trust" rend l\'escrow aussi simple que possible pour tous.' : 'Our "3 Taps to Trust" philosophy makes escrow as simple as possible for everyone.' },
    { icon: '🌍', title: language === 'fr' ? 'Afrique' : 'Africa', description: language === 'fr' ? 'Construit pour l\'Afrique, optimisé pour le Cameroun. Nous comprenons les besoins locaux.' : 'Built for Africa, optimized for Cameroon. We understand local needs.' },
  ];

  const stats = [
    { number: '12,500+', label: language === 'fr' ? 'Transactions' : 'Transactions' },
    { number: '5,200+', label: language === 'fr' ? 'Utilisateurs' : 'Users' },
    { number: '98%', label: language === 'fr' ? 'Taux de Réussite' : 'Success Rate' },
    { number: '50M+', label: language === 'fr' ? 'XAF Sécurisés' : 'XAF Secured' },
  ];

  return (
    <PublicPageLayout>
      <SEO title={language === 'fr' ? 'À Propos de WekaTrust' : 'About WekaTrust'} structuredData={generateOrganizationSchema()} />
      <button type="button" onClick={() => navigate('/')} className="public-page-back">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        {language === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
      </button>

      <div className="public-page-hero">
        <h1 className="public-page-title">{language === 'fr' ? 'À Propos de WekaTrust' : 'About WekaTrust'}</h1>
        <p className="public-page-sub">
          {language === 'fr' ? 'Sécuriser la confiance dans le commerce numérique en Afrique' : 'Securing trust in digital commerce across Africa'}
        </p>
      </div>

      <div className="public-page-section">
        <div className="public-page-card">
          <h2 className="public-page-section-title">{language === 'fr' ? 'Notre Histoire' : 'Our Story'}</h2>
          <p className="public-page-card-desc" style={{ marginBottom: '1rem' }}>
            {language === 'fr'
              ? 'WekaTrust est né de la nécessité de résoudre le problème de confiance dans le commerce numérique en Afrique. Nous avons vu trop de personnes perdre de l\'argent à cause de transactions non sécurisées sur les réseaux sociaux et les plateformes de commerce électronique.'
              : 'WekaTrust was born from the need to solve the trust problem in digital commerce across Africa. We saw too many people lose money due to unsecured transactions on social media and e-commerce platforms.'}
          </p>
          <p className="public-page-card-desc">
            {language === 'fr'
              ? 'Notre mission est de rendre les transactions sécurisées accessibles à tous, en utilisant la technologie mobile que la plupart des Africains utilisent déjà.'
              : 'Our mission is to make secure transactions accessible to everyone, using the mobile technology that most Africans already use.'}
          </p>
        </div>
      </div>

      <div className="public-page-grid" style={{ marginBottom: '2rem' }}>
        <div className="public-page-card">
          <div className="public-page-contact-icon">🎯</div>
          <h3 className="public-page-card-title">{language === 'fr' ? 'Notre Mission' : 'Our Mission'}</h3>
          <p className="public-page-card-desc">
            {language === 'fr'
              ? 'Rendre les transactions sécurisées accessibles à tous en Afrique, en utilisant la technologie mobile pour construire la confiance.'
              : 'Make secure transactions accessible to everyone in Africa, using mobile technology to build trust in digital commerce.'}
          </p>
        </div>
        <div className="public-page-card">
          <div className="public-page-contact-icon">👁️</div>
          <h3 className="public-page-card-title">{language === 'fr' ? 'Notre Vision' : 'Our Vision'}</h3>
          <p className="public-page-card-desc">
            {language === 'fr'
              ? 'Devenir la plateforme d\'escrow de référence en Afrique, où chaque transaction numérique est sécurisée.'
              : 'To become Africa\'s leading escrow platform, where every digital transaction is secured and every user can trust.'}
          </p>
        </div>
      </div>

      <div className="public-page-section">
        <h2 className="public-page-section-title" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>{language === 'fr' ? 'Nos Valeurs' : 'Our Values'}</h2>
        <div className="public-page-grid">
          {values.map((v, i) => (
            <div key={i} className="public-page-card">
              <div className="public-page-contact-icon">{v.icon}</div>
              <h3 className="public-page-card-title">{v.title}</h3>
              <p className="public-page-card-desc">{v.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="public-page-grid" style={{ marginBottom: '2rem' }}>
        {stats.map((s, i) => (
          <div key={i} className="public-page-stat">
            <div className="public-page-stat-num">{s.number}</div>
            <div className="public-page-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="public-page-card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2 className="public-page-section-title" style={{ marginBottom: '0.5rem' }}>{language === 'fr' ? 'Rejoignez-Nous' : 'Join Us'}</h2>
        <p className="public-page-card-desc" style={{ marginBottom: '1.5rem' }}>
          {language === 'fr' ? 'Faites partie de la révolution du commerce numérique sécurisé en Afrique' : 'Be part of the secure digital commerce revolution in Africa'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
          <button type="button" onClick={() => navigate('/auth/signup')} className="public-page-cta">
            {language === 'fr' ? 'Créer un Compte' : 'Create Account'}
          </button>
          <button type="button" onClick={() => navigate('/contact')} className="public-page-cta" style={{ background: 'transparent', color: 'var(--pub-green)', border: '1px solid var(--pub-green)' }}>
            {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
          </button>
        </div>
      </div>
    </PublicPageLayout>
  );
};

export default About;
